import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import ContactFormFields, {
  getContactFormValidationError,
} from "@/components/commerce/ContactFormFields";
import ActaModal from "@/components/ui/ActaModal";
import { formatApiError } from "@/lib/api";


const EMPTY_FORM = {
  machine_catalog_id: "",
  prospect_id: "",
  parent_machine_id: "",
  equipment_type: "machine",
  location_status: "in_stock",
  stock_location: "",
  stock_received_at: "",
  name: "",
  serial_number: "",
  manufacturer: "",
  model_name: "",
  status: "active",
  site_name: "",
  address: "",
  city: "",
  postal_code: "",
  country: "FR",
  commissioning_date: "",
  warranty_end_date: "",
  notes: "",
};


const EMPTY_QUICK_CLIENT_FORM = {
  company: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  position: "",
  address: "",
  postalCode: "",
  city: "",
  country: "FR",
  lat: null,
  lng: null,
  lifecycleStage: "client",
};


const MACHINE_STATUSES = [
  {
    value: "active",
    label: "Active",
  },
  {
    value: "maintenance",
    label: "Maintenance",
  },
  {
    value: "inactive",
    label: "Inactive",
  },
  {
    value: "retired",
    label: "Retirée",
  },
];


const LOCATION_STATUSES = [
  {
    value: "in_stock",
    label: "En stock",
  },
  {
    value: "reserved",
    label: "Réservée",
  },
  {
    value: "installed",
    label: "Installée chez un client",
  },
];


function normalizeDate(value) {
  if (!value) {
    return "";
  }

  return String(value).slice(0, 10);
}


function prospectLabel(prospect) {
  const contactName = [
    prospect.firstName,
    prospect.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  if (
    prospect.company &&
    contactName
  ) {
    return `${prospect.company} — ${contactName}`;
  }

  return (
    prospect.company ||
    contactName ||
    prospect.email ||
    prospect.id
  );
}


const CATALOG_TYPE_LABELS = {
  machine: "Machine",
  accessory: "Accessoire",
  handpiece: "Pièce à main",
};


function catalogItemLabel(item) {
  const identity = [
    item.name,
    item.modelName,
  ]
    .filter(Boolean)
    .join(" — ") || item.id;

  return `${
    CATALOG_TYPE_LABELS[
      item.type || "machine"
    ] || "Équipement"
  } — ${identity}`;
}


function initialForm(
  mode,
  machine
) {
  if (
    mode !== "edit" ||
    !machine
  ) {
    return {
      ...EMPTY_FORM,
    };
  }

  return {
    machine_catalog_id:
      machine.machineCatalogId || "",
    prospect_id:
      machine.prospectId || "",
    parent_machine_id:
      machine.parentMachineId || "",
    equipment_type:
      machine.equipmentType || "machine",
    location_status:
      machine.locationStatus ||
      (
        machine.prospectId
          ? "installed"
          : "in_stock"
      ),
    stock_location:
      machine.stockLocation || "",
    stock_received_at:
      normalizeDate(
        machine.stockReceivedAt
      ),
    name:
      machine.name || "",
    serial_number:
      machine.serialNumber || "",
    manufacturer:
      machine.manufacturer || "",
    model_name:
      machine.model || "",
    status:
      machine.status || "active",
    site_name:
      machine.siteName || "",
    address:
      machine.address || "",
    city:
      machine.city || "",
    postal_code:
      machine.postalCode || "",
    country:
      machine.country || "FR",
    commissioning_date:
      normalizeDate(
        machine.commissioningDate
      ),
    warranty_end_date:
      normalizeDate(
        machine.warrantyEndDate
      ),
    notes:
      machine.notes || "",
  };
}


export default function MachineFormModal({
  open,
  mode = "create",
  machine = null,
  prospects = [],
  machines = [],
  catalogItems = [],
  catalogLoadError = "",
  onReloadCatalog = null,
  onCreateClient = null,
  initialParentMachineId = "",
  fixedParentMachineId = "",
  initialProspectId = "",
  initialLocationStatus = "",
  fixedLocationStatus = "",
  fixedEquipmentType = "",
  saving = false,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(
    EMPTY_FORM
  );
  const [
    quickClientOpen,
    setQuickClientOpen,
  ] = useState(false);
  const [
    quickClientForm,
    setQuickClientForm,
  ] = useState(EMPTY_QUICK_CLIENT_FORM);
  const [
    quickClientSaving,
    setQuickClientSaving,
  ] = useState(false);
  const [
    quickClientError,
    setQuickClientError,
  ] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextForm = initialForm(
      mode,
      machine
    );

    if (mode === "create") {
      nextForm.parent_machine_id =
        fixedParentMachineId ||
        initialParentMachineId ||
        "";
      nextForm.prospect_id =
        initialProspectId || "";
      nextForm.location_status =
        fixedLocationStatus ||
        initialLocationStatus ||
        nextForm.location_status;
      if (fixedEquipmentType) {
        nextForm.equipment_type =
          fixedEquipmentType;
      }
    }

    setForm(nextForm);
    setQuickClientOpen(false);
    setQuickClientForm(
      EMPTY_QUICK_CLIENT_FORM
    );
    setQuickClientError("");
  }, [
    open,
    mode,
    machine,
    initialParentMachineId,
    fixedParentMachineId,
    initialProspectId,
    initialLocationStatus,
    fixedLocationStatus,
    fixedEquipmentType,
  ]);


  const sortedCatalogItems = useMemo(
    () =>
      [...catalogItems]
        .filter(
          (item) =>
            item.active !== false &&
            [
              "machine",
              "accessory",
              "handpiece",
            ].includes(
              item.type || "machine"
            ) &&
            (
              !fixedEquipmentType ||
              (item.type || "machine") ===
                fixedEquipmentType
            )
        )
        .sort(
          (left, right) =>
            catalogItemLabel(left).localeCompare(
              catalogItemLabel(right),
              "fr",
              {
                sensitivity: "base",
              }
            )
        ),
    [catalogItems, fixedEquipmentType]
  );

  const sortedParentMachines = useMemo(
    () =>
      [...machines]
        .filter(
          (item) =>
            item &&
            item.isArchived !== true &&
            (item.equipmentType || "machine") === "machine"
        )
        .sort(
          (left, right) =>
            String(left.name || "").localeCompare(
              String(right.name || ""),
              "fr",
              { sensitivity: "base" }
            )
        ),
    [machines]
  );

  const sortedClients = useMemo(
    () =>
      [...prospects].sort(
        (left, right) =>
          prospectLabel(left).localeCompare(
            prospectLabel(right),
            "fr",
            {
              sensitivity: "base",
            }
          )
      ),
    [prospects]
  );


  if (!open) {
    return null;
  }


  const catalogIdentityLocked =
    mode === "edit" &&
    Boolean(machine?.machineCatalogId);

  const selectedCatalogItem =
    sortedCatalogItems.find(
      (item) =>
        item.id ===
        form.machine_catalog_id
    ) || null;

  const compatibleIds =
    selectedCatalogItem?.compatibleMachineIds || [];

  const compatibleParentMachines =
    mode === "create" &&
    selectedCatalogItem?.type === "handpiece"
      ? (
          compatibleIds.length
            ? sortedParentMachines.filter(
                (item) =>
                  item.machineCatalogId &&
                  compatibleIds.includes(
                    item.machineCatalogId
                  )
              )
            : sortedParentMachines
        )
      : [];

  const locationStatus =
    fixedLocationStatus ||
    form.location_status ||
    (
      form.prospect_id
        ? "installed"
        : "in_stock"
    );

  const requiresClient =
    [
      "reserved",
      "installed",
    ].includes(locationStatus);

  const allowsClient =
    [
      "installed",
      "reserved",
    ].includes(locationStatus);

  const usesStockLocation =
    locationStatus !== "installed";


  const updateField = (
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };


  const createQuickClient = async () => {
    if (
      !onCreateClient ||
      quickClientSaving
    ) {
      return;
    }

    const validationError =
      getContactFormValidationError(
        quickClientForm
      );

    if (validationError) {
      setQuickClientError(
        validationError
      );
      return;
    }

    setQuickClientSaving(true);
    setQuickClientError("");

    try {
      const created =
        await onCreateClient({
          company:
            quickClientForm.company.trim(),
          first_name:
            quickClientForm.firstName.trim(),
          last_name:
            quickClientForm.lastName.trim(),
          email:
            quickClientForm.email.trim(),
          phone:
            quickClientForm.phone.trim(),
          position:
            quickClientForm.position.trim(),
          address:
            quickClientForm.address.trim(),
          postal_code:
            quickClientForm.postalCode.trim(),
          city:
            quickClientForm.city.trim(),
          country:
            quickClientForm.country || "FR",
        });

      if (!created?.id) {
        throw new Error(
          "Client créé sans identifiant exploitable."
        );
      }

      setForm((current) => ({
        ...current,
        prospect_id: created.id,
      }));
      setQuickClientForm(
        EMPTY_QUICK_CLIENT_FORM
      );
      setQuickClientOpen(false);
      toast.success(
        "Client créé et sélectionné"
      );
    } catch (requestError) {
      setQuickClientError(
        formatApiError(
          requestError.response?.data?.detail
        ) ||
        requestError.message ||
        "Impossible de créer le client."
      );
    } finally {
      setQuickClientSaving(false);
    }
  };


  const submit = (event) => {
    event.preventDefault();

    const name =
      mode === "create"
        ? (
            selectedCatalogItem?.name ||
            ""
          ).trim()
        : form.name.trim();

    const serialNumber =
      form.serial_number.trim();

    const rawProspectId =
      form.prospect_id.trim();

    const prospectId =
      locationStatus === "in_stock"
        ? null
        : rawProspectId || null;

    if (
      requiresClient &&
      !prospectId
    ) {
      toast.error(
        locationStatus === "reserved"
          ? "Sélectionnez le client auquel l'équipement est réservé."
          : "Sélectionnez le client chez lequel l'équipement est installé."
      );

      return;
    }

    if (
      mode === "create" &&
      !selectedCatalogItem
    ) {
      toast.error(
        "Sélectionnez un équipement dans le catalogue."
      );

      return;
    }

    if (!name) {
      toast.error(
        "Le nom de l'équipement est obligatoire."
      );

      return;
    }

    if (!serialNumber) {
      toast.error(
        "Le numéro de série est obligatoire."
      );

      return;
    }

    if (
      mode === "edit" &&
      form.commissioning_date &&
      form.warranty_end_date &&
      form.warranty_end_date < form.commissioning_date
    ) {
      toast.error(
        "La fin de garantie ne peut pas être antérieure à la mise en service."
      );

      return;
    }

    onSubmit({
      machine_catalog_id:
        mode === "create"
          ? selectedCatalogItem.id
          : form.machine_catalog_id || null,
      prospect_id: prospectId,
      ...(mode === "create" &&
      ["accessory", "handpiece"].includes(
        selectedCatalogItem.type
      )
        ? {
            parent_machine_id:
              fixedParentMachineId ||
              form.parent_machine_id ||
              null,
          }
        : {}),
      equipment_type:
        mode === "create"
          ? selectedCatalogItem.type || "machine"
          : form.equipment_type || "machine",
      location_status: locationStatus,
      stock_location:
        form.stock_location.trim(),
      stock_received_at:
        form.stock_received_at || null,
      name,
      serial_number: serialNumber,
      manufacturer:
        mode === "create"
          ? (
              selectedCatalogItem.manufacturer ||
              ""
            ).trim()
          : form.manufacturer.trim(),
      model_name:
        mode === "create"
          ? (
              selectedCatalogItem.modelName ||
              ""
            ).trim()
          : form.model_name.trim(),
      status: form.status,
      site_name:
        form.site_name.trim(),
      address:
        form.address.trim(),
      city:
        form.city.trim(),
      postal_code:
        form.postal_code.trim(),
      country:
        form.country || "FR",
      commissioning_date:
        form.commissioning_date || null,
      ...(mode === "edit"
        ? {
            warranty_end_date:
              form.warranty_end_date || null,
          }
        : {}),
      notes:
        form.notes.trim(),
    });
  };

  const inputClass =
    "w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm outline-none transition focus:border-[var(--do-accent)] disabled:cursor-not-allowed disabled:opacity-60";


  if (mode === "create") {
    return (
      <ActaModal
        open={open}
        onClose={onClose}
        eyebrow="GMAO"
        title="Nouvel équipement"
        subtitle={
          fixedEquipmentType === "accessory"
            ? "Ajoutez un accessoire à la machine."
            : fixedEquipmentType === "handpiece"
              ? "Ajoutez une pièce à main à la machine."
              : "Renseignez uniquement les informations essentielles."
        }
        size="lg"
        closeDisabled={saving}
        testId="machine-form-modal"
      >
        <form
          onSubmit={submit}
          className="space-y-5"
          data-testid="minimal-equipment-create-form"
        >
            <div>
              <label
                htmlFor="machine-catalog-item"
                className="label-eyebrow mb-1.5 block"
              >
                Équipement du catalogue *
              </label>

              <select
                id="machine-catalog-item"
                value={form.machine_catalog_id}
                onChange={(event) => {
                  const catalogId =
                    event.target.value;
                  const item =
                    sortedCatalogItems.find(
                      (candidate) =>
                        candidate.id ===
                        catalogId
                    );

                  setForm((current) => ({
                    ...current,
                    machine_catalog_id:
                      catalogId,
                    equipment_type:
                      fixedEquipmentType ||
                      item?.type ||
                      "machine",
                    name:
                      item?.name || "",
                    manufacturer:
                      item?.manufacturer || "",
                    model_name:
                      item?.modelName || "",
                    parent_machine_id:
                      fixedParentMachineId ||
                      (
                        item?.type === "handpiece" &&
                        (
                          !(item.compatibleMachineIds || []).length ||
                          sortedParentMachines.some(
                            (parentMachine) =>
                              parentMachine.id ===
                                current.parent_machine_id &&
                              (item.compatibleMachineIds || []).includes(
                                parentMachine.machineCatalogId
                              )
                          )
                        )
                          ? current.parent_machine_id
                          : ""
                      ),
                  }));
                }}
                disabled={saving}
                data-testid="machine-catalog-select"
                className={inputClass}
                autoFocus
              >
                <option value="">
                  Sélectionner un équipement
                </option>

                {sortedCatalogItems.map(
                  (item) => (
                    <option
                      key={item.id}
                      value={item.id}
                    >
                      {catalogItemLabel(item)}
                    </option>
                  )
                )}
              </select>

              {catalogLoadError && (
                <div
                  className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] p-3 text-xs text-[var(--do-danger)]"
                  data-testid="machine-catalog-error"
                >
                  <span>
                    Impossible de charger le catalogue.
                  </span>

                  {onReloadCatalog && (
                    <button
                      type="button"
                      onClick={onReloadCatalog}
                      disabled={saving}
                      className="font-bold underline"
                    >
                      Réessayer
                    </button>
                  )}
                </div>
              )}

              {!catalogLoadError &&
                sortedCatalogItems.length === 0 && (
                  <p
                    className="mt-1.5 text-xs text-[var(--do-danger)]"
                    data-testid="machine-catalog-empty"
                  >
                    Aucun équipement actif compatible n'est disponible dans le catalogue.
                  </p>
                )}

            </div>

            {selectedCatalogItem?.type === "handpiece" &&
              !fixedParentMachineId && (
              <div>
                <label
                  htmlFor="handpiece-parent-machine"
                  className="label-eyebrow mb-1.5 block"
                >
                  Machine principale
                </label>

                <select
                  id="handpiece-parent-machine"
                  value={form.parent_machine_id}
                  onChange={(event) =>
                    updateField(
                      "parent_machine_id",
                      event.target.value
                    )
                  }
                  disabled={saving}
                  data-testid="handpiece-parent-machine-create-select"
                  className={inputClass}
                >
                  <option value="">
                    Aucune — pièce indépendante
                  </option>

                  {compatibleParentMachines.map(
                    (parentMachine) => (
                      <option
                        key={parentMachine.id}
                        value={parentMachine.id}
                      >
                        {parentMachine.name || parentMachine.id}
                        {parentMachine.serialNumber
                          ? ` — ${parentMachine.serialNumber}`
                          : ""}
                      </option>
                    )
                  )}
                </select>

                <p className="mt-1.5 text-xs text-[var(--do-muted)]">
                  Le rattachement est facultatif et pourra être modifié plus tard.
                </p>
              </div>
            )}

            <div>
              <label
                htmlFor="machine-location-status"
                className="label-eyebrow mb-1.5 block"
              >
                Situation *
              </label>

              <select
                id="machine-location-status"
                value={locationStatus}
                onChange={(event) => {
                  const nextStatus =
                    event.target.value;

                  setForm((current) => ({
                    ...current,
                    location_status:
                      nextStatus,
                    prospect_id:
                      nextStatus ===
                      "in_stock"
                        ? ""
                        : current.prospect_id,
                    stock_location:
                      nextStatus === "installed"
                        ? ""
                        : current.stock_location,
                    stock_received_at:
                      nextStatus === "in_stock"
                        ? current.stock_received_at
                        : "",
                    site_name:
                      nextStatus === "installed"
                        ? current.site_name
                        : "",
                    address:
                      nextStatus === "installed"
                        ? current.address
                        : "",
                    city:
                      nextStatus === "installed"
                        ? current.city
                        : "",
                    postal_code:
                      nextStatus === "installed"
                        ? current.postal_code
                        : "",
                    commissioning_date:
                      nextStatus === "installed"
                        ? current.commissioning_date
                        : "",
                  }));
                }}
                disabled={
                  saving
                  || Boolean(
                    fixedLocationStatus
                  )
                }
                data-testid="machine-location-status-select"
                className={inputClass}
              >
                {LOCATION_STATUSES.map(
                  (status) => (
                    <option
                      key={status.value}
                      value={status.value}
                    >
                      {status.label}
                    </option>
                  )
                )}
              </select>
            </div>

            {allowsClient && (
              <div>
                <div className="flex items-center justify-between gap-3">
                  <label
                    htmlFor="machine-client"
                    className="label-eyebrow block"
                  >
                    Client
                    {requiresClient ? " *" : ""}
                  </label>

                  {locationStatus === "installed" &&
                    onCreateClient && (
                      <button
                        type="button"
                        onClick={() => {
                          setQuickClientOpen(
                            (current) => !current
                          );
                          setQuickClientError("");
                        }}
                        disabled={
                          saving ||
                          quickClientSaving
                        }
                        className="text-xs font-heading font-bold text-[var(--do-accent)] disabled:opacity-50"
                        data-testid="machine-quick-client-toggle"
                      >
                        {quickClientOpen
                          ? "Annuler"
                          : "+ Créer un client"}
                      </button>
                    )}
                </div>

                <select
                  id="machine-client"
                  value={form.prospect_id}
                  onChange={(event) =>
                    updateField(
                      "prospect_id",
                      event.target.value
                    )
                  }
                  disabled={
                    saving ||
                    quickClientSaving
                  }
                  data-testid="machine-client-select"
                  className={inputClass}
                >
                  <option value="">
                    Sélectionner le client
                  </option>

                  {sortedClients.map(
                    (client) => (
                      <option
                        key={client.id}
                        value={client.id}
                      >
                        {prospectLabel(client)}
                      </option>
                    )
                  )}
                </select>

                {quickClientOpen &&
                  locationStatus === "installed" && (
                    <section
                      className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] p-4"
                      data-testid="machine-quick-client-form"
                      onKeyDown={(event) => {
                        if (
                          event.key === "Enter"
                        ) {
                          event.preventDefault();
                          createQuickClient();
                        }
                      }}
                    >
                      <div className="mb-3">
                        <div className="font-heading text-sm font-black">
                          Nouveau client
                        </div>
                        <p className="mt-1 text-xs text-[var(--do-muted)]">
                          Le client sera créé sans commercial affecté, puis sélectionné pour cet équipement.
                        </p>
                      </div>

                      {quickClientError ? (
                        <div
                          className="mb-3 rounded-lg border border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] p-3 text-xs text-[var(--do-danger)]"
                          role="alert"
                        >
                          {quickClientError}
                        </div>
                      ) : null}

                      <ContactFormFields
                        value={quickClientForm}
                        onChange={
                          setQuickClientForm
                        }
                        showLifecycleStage={false}
                      />

                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={
                            createQuickClient
                          }
                          disabled={
                            saving ||
                            quickClientSaving
                          }
                          className="rounded-lg bg-[var(--do-accent)] px-4 py-2.5 text-sm font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
                          data-testid="machine-quick-client-submit"
                        >
                          {quickClientSaving
                            ? "Création…"
                            : "Créer et sélectionner"}
                        </button>
                      </div>
                    </section>
                  )}

                {requiresClient && (
                  <p className="mt-1.5 text-xs text-[var(--do-muted)]">
                    Un client est obligatoire pour un équipement réservé ou installé.
                  </p>
                )}
              </div>
            )}

            {locationStatus === "in_stock" && (
              <div>
                <label
                  htmlFor="machine-stock-received-at"
                  className="label-eyebrow mb-1.5 block"
                >
                  Date d'entrée en stock
                </label>

                <input
                  id="machine-stock-received-at"
                  type="date"
                  value={form.stock_received_at}
                  onChange={(event) =>
                    updateField(
                      "stock_received_at",
                      event.target.value
                    )
                  }
                  disabled={saving}
                  data-testid="machine-stock-received-at-input"
                  className={inputClass}
                />
              </div>
            )}

            {locationStatus === "installed" && (
              <div>
                <label
                  htmlFor="machine-commissioning-date"
                  className="label-eyebrow mb-1.5 block"
                >
                  Date de mise en service
                </label>

                <input
                  id="machine-commissioning-date"
                  type="date"
                  value={form.commissioning_date}
                  onChange={(event) =>
                    updateField(
                      "commissioning_date",
                      event.target.value
                    )
                  }
                  disabled={saving}
                  data-testid="machine-commissioning-date-input"
                  className={inputClass}
                />
              </div>
            )}

            <div>
              <label
                htmlFor="machine-serial"
                className="label-eyebrow mb-1.5 block"
              >
                Numéro de série *
              </label>

              <input
                id="machine-serial"
                type="text"
                value={form.serial_number}
                onChange={(event) =>
                  updateField(
                    "serial_number",
                    event.target.value
                  )
                }
                disabled={saving}
                maxLength={160}
                placeholder="Saisir le numéro de série"
                data-testid="machine-serial-input"
                className={inputClass}
              />
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-[var(--do-border)] pt-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="rounded-lg border border-[var(--do-border)] px-4 py-2.5 text-sm font-heading font-semibold transition hover:bg-[var(--do-surface-soft)] disabled:opacity-50"
              >
                Annuler
              </button>

              <button
                type="submit"
                disabled={
                  saving ||
                  quickClientSaving
                }
                data-testid="machine-submit"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--do-accent)] px-4 py-2.5 text-sm font-heading font-bold text-[var(--do-on-accent)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Check
                  size={18}
                  weight="bold"
                />

                {saving
                  ? "Création…"
                  : "Créer l'équipement"}
              </button>
            </div>
        </form>
      </ActaModal>
    );
  }


  return (
    <ActaModal
      open={open}
      onClose={onClose}
      eyebrow="GMAO"
      title="Modifier l'équipement"
      size="xl"
      closeDisabled={saving}
      testId="machine-form-modal"
    >
      <form
        onSubmit={submit}
        className="space-y-5"
        data-testid="equipment-edit-form"
      >
          <div>
            <div className="label-eyebrow mb-2">
              Type d'équipement *
            </div>

            <div
              className="grid grid-cols-1 gap-3 sm:grid-cols-3"
              data-testid="equipment-type-selector"
            >
              {[
                {
                  value: "machine",
                  label: "Machine",
                  description:
                    "Équipement principal vendu au client.",
                },
                {
                  value: "accessory",
                  label: "Accessoire",
                  description:
                    "Accessoire indépendant avec son propre numéro de série.",
                },
                {
                  value: "handpiece",
                  label: "Pièce à main",
                  description:
                    "Équipement sérialisé pouvant être vendu, installé, réparé ou remplacé.",
                },
              ].map((equipmentType) => {
                const selected =
                  form.equipment_type ===
                  equipmentType.value;

                return (
                  <button
                    key={equipmentType.value}
                    type="button"
                    onClick={() =>
                      updateField(
                        "equipment_type",
                        equipmentType.value
                      )
                    }
                    disabled={saving}
                    data-testid={
                      `equipment-type-${equipmentType.value}`
                    }
                    aria-pressed={selected}
                    className={[
                      "rounded-xl border p-4 text-left transition",
                      selected
                        ? "border-[var(--do-accent)] bg-[var(--do-surface-soft)] ring-1 ring-[var(--do-accent)]"
                        : "border-[var(--do-border)] hover:bg-[var(--do-surface-soft)]",
                      saving
                        ? "cursor-not-allowed opacity-60"
                        : "",
                    ].join(" ")}
                  >
                    <div className="flex items-center gap-2 font-heading text-sm font-bold">
                      <span
                        className={[
                          "flex h-4 w-4 items-center justify-center rounded-full border",
                          selected
                            ? "border-[var(--do-accent)]"
                            : "border-[var(--do-border)]",
                        ].join(" ")}
                      >
                        {selected && (
                          <span className="h-2 w-2 rounded-full bg-[var(--do-accent)]" />
                        )}
                      </span>

                      {equipmentType.label}
                    </div>

                    <p className="mt-2 text-xs leading-5 text-[var(--do-muted)]">
                      {equipmentType.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          <div
            className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4 text-sm"
            data-testid="equipment-assignment-managed-separately"
          >
            <div className="font-heading font-bold">
              Affectation et situation
            </div>

            <p className="mt-1 text-[var(--do-muted)]">
              Utilisez l’action « Affecter à un client » ou
              « Changer le client » depuis la liste des équipements.
            </p>
          </div>

          {usesStockLocation && (
            <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4">
              <div className="font-heading text-sm font-bold">
                Stock
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="machine-stock-location"
                    className="label-eyebrow mb-1.5 block"
                  >
                    Emplacement de stockage
                  </label>

                  <input
                    id="machine-stock-location"
                    type="text"
                    value={form.stock_location}
                    onChange={(event) =>
                      updateField(
                        "stock_location",
                        event.target.value
                      )
                    }
                    disabled={saving}
                    maxLength={240}
                    placeholder="Entrepôt A · Zone 3"
                    data-testid="machine-stock-location-input"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label
                    htmlFor="machine-stock-received-at"
                    className="label-eyebrow mb-1.5 block"
                  >
                    Date d’entrée en stock
                  </label>

                  <input
                    id="machine-stock-received-at"
                    type="date"
                    value={form.stock_received_at}
                    onChange={(event) =>
                      updateField(
                        "stock_received_at",
                        event.target.value
                      )
                    }
                    disabled={saving}
                    data-testid="machine-stock-received-at-input"
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="machine-name"
                className="label-eyebrow mb-1.5 block"
              >
                Nom de l'équipement *
              </label>

              <input
                id="machine-name"
                type="text"
                value={form.name}
                onChange={(event) =>
                  updateField(
                    "name",
                    event.target.value
                  )
                }
                disabled={
                  saving ||
                  catalogIdentityLocked
                }
                maxLength={160}
                placeholder="Presse hydraulique 01"
                data-testid="machine-name-input"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="machine-serial"
                className="label-eyebrow mb-1.5 block"
              >
                Numéro de série *
              </label>

              <input
                id="machine-serial"
                type="text"
                value={form.serial_number}
                onChange={(event) =>
                  updateField(
                    "serial_number",
                    event.target.value
                  )
                }
                disabled={saving}
                maxLength={160}
                placeholder="SN-2026-0001"
                data-testid="machine-serial-input"
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="machine-model"
                className="label-eyebrow mb-1.5 block"
              >
                Modèle
              </label>

              <input
                id="machine-model"
                type="text"
                value={form.model_name}
                onChange={(event) =>
                  updateField(
                    "model_name",
                    event.target.value
                  )
                }
                disabled={
                  saving ||
                  catalogIdentityLocked
                }
                data-testid="machine-model-input"
                className={inputClass}
              />
            </div>

            <div
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4 text-sm"
              data-testid="equipment-status-managed-separately"
            >
              <div className="font-heading font-bold">
                État technique
              </div>

              <p className="mt-1 text-[var(--do-muted)]">
                Utilisez l’action « Modifier l’état » depuis
                la liste des équipements.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4">
            <div className="font-heading text-sm font-bold">
              Localisation
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="machine-site"
                  className="label-eyebrow mb-1.5 block"
                >
                  Nom du site
                </label>

                <input
                  id="machine-site"
                  type="text"
                  value={form.site_name}
                  onChange={(event) =>
                    updateField(
                      "site_name",
                      event.target.value
                    )
                  }
                  disabled={saving}
                  data-testid="machine-site-input"
                  className={inputClass}
                />
              </div>

              <div>
                <label
                  htmlFor="machine-address"
                  className="label-eyebrow mb-1.5 block"
                >
                  Adresse
                </label>

                <input
                  id="machine-address"
                  type="text"
                  value={form.address}
                  onChange={(event) =>
                    updateField(
                      "address",
                      event.target.value
                    )
                  }
                  disabled={saving}
                  data-testid="machine-address-input"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label
                  htmlFor="machine-postal-code"
                  className="label-eyebrow mb-1.5 block"
                >
                  Code postal
                </label>

                <input
                  id="machine-postal-code"
                  type="text"
                  value={form.postal_code}
                  onChange={(event) =>
                    updateField(
                      "postal_code",
                      event.target.value
                    )
                  }
                  disabled={saving}
                  data-testid="machine-postal-code-input"
                  className={inputClass}
                />
              </div>

              <div>
                <label
                  htmlFor="machine-city"
                  className="label-eyebrow mb-1.5 block"
                >
                  Ville
                </label>

                <input
                  id="machine-city"
                  type="text"
                  value={form.city}
                  onChange={(event) =>
                    updateField(
                      "city",
                      event.target.value
                    )
                  }
                  disabled={saving}
                  data-testid="machine-city-input"
                  className={inputClass}
                />
              </div>

              <div>
                <label
                  htmlFor="machine-country"
                  className="label-eyebrow mb-1.5 block"
                >
                  Pays
                </label>

                <select
                  id="machine-country"
                  value={form.country}
                  onChange={(event) =>
                    updateField(
                      "country",
                      event.target.value
                    )
                  }
                  disabled={saving}
                  data-testid="machine-country-select"
                  className={inputClass}
                >
                  <option value="FR">
                    France
                  </option>

                  <option value="BE">
                    Belgique
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="machine-commissioning-date"
                className="label-eyebrow mb-1.5 block"
              >
                Mise en service
              </label>

              <input
                id="machine-commissioning-date"
                type="date"
                value={form.commissioning_date}
                onChange={(event) =>
                  updateField(
                    "commissioning_date",
                    event.target.value
                  )
                }
                disabled={saving}
                data-testid="machine-commissioning-date-input"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="machine-warranty-date"
                className="label-eyebrow mb-1.5 block"
              >
                Fin de garantie
              </label>

              <input
                id="machine-warranty-date"
                type="date"
                value={form.warranty_end_date}
                onChange={(event) =>
                  updateField(
                    "warranty_end_date",
                    event.target.value
                  )
                }
                disabled={saving}
                data-testid="machine-warranty-date-input"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="machine-notes"
              className="label-eyebrow mb-1.5 block"
            >
              Notes
            </label>

            <textarea
              id="machine-notes"
              value={form.notes}
              onChange={(event) =>
                updateField(
                  "notes",
                  event.target.value
                )
              }
              disabled={saving}
              rows={4}
              maxLength={4000}
              placeholder="Informations techniques, consignes ou remarques…"
              data-testid="machine-notes-input"
              className={`${inputClass} resize-y`}
            />
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-[var(--do-border)] pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-[var(--do-border)] px-4 py-2 text-sm font-heading font-semibold disabled:opacity-50"
            >
              Annuler
            </button>

            <button
              type="submit"
              disabled={saving}
              data-testid="machine-form-submit"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--do-accent)] px-4 py-2 text-sm font-heading font-bold text-[var(--do-on-accent)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Check
                size={17}
                weight="bold"
              />

              {saving
                ? "Enregistrement…"
                : mode === "create"
                  ? "Créer la machine"
                  : "Enregistrer"}
            </button>
          </div>
      </form>
    </ActaModal>
  );
}
